import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import '@fontsource-variable/inter';
import '../styles/landing.css';
import { APP_LINKS, LEGAL, LEGAL_ADDRESS, LIMITS } from '@chefty/shared-types/constants';
import { useLandingTheme, type Theme } from '../hooks/useLandingTheme';
import { useLandingLang } from '../hooks/useLandingLang';
import { useScrollDepth } from '../hooks/useScrollDepth';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useSectionViews } from '../hooks/useSectionViews';
import { analyticsAvailable, track } from '../services/analytics';
import { openConsentSettings } from '../services/consent';
import { COPY, type Copy } from './landing/copy';
import { LangMenu } from './landing/LangMenu';
import { Carousel } from './landing/Carousel';
import { CookDemo } from './landing/CookDemo';
import { HeroSteps } from './landing/HeroSteps';
import { ImportDemo } from './landing/ImportDemo';

/*
 * Marketing landing, direction "Kitchen table" (brand book, October 2026), in English
 * and German (words in landing/copy.ts).
 *
 * Every claim is checked against the app. The example week is what apps/mobile/src/prep
 * builds for the default profile (Stay fit, 1 person, 5 lunches, prep day Sunday) with the
 * bases teriyaki, lentils and quinoa. Protein values are the per-base estimates from
 * basen.ts, so each one carries "Estimated per portion" and the page never says
 * "high protein" (EU 1924/2006). Imported recipes don't feed the prep week yet, and the
 * page says so. The free allowance is the shared LIMITS constant; the Plus prices mirror
 * apps/mobile/src/purchases/plans.ts (eurozone list prices, the store localises them).
 */

const PLUS_MONTHLY = 4.99;
const PLUS_YEARLY = 29.99;
const TRIAL_DAYS = 7;

const euro = (amount: number, c: Copy) =>
  new Intl.NumberFormat(c.locale, { style: 'currency', currency: 'EUR' }).format(amount);

/** The best way in that exists today: the App Store, then TestFlight, then a beta invite by mail. */
const CHANNEL: 'appStore' | 'testflight' | 'invite' = APP_LINKS.appStore
  ? 'appStore'
  : APP_LINKS.testflight
    ? 'testflight'
    : 'invite';

const getAppHref = (c: Copy) =>
  CHANNEL === 'appStore'
    ? APP_LINKS.appStore
    : CHANNEL === 'testflight'
      ? APP_LINKS.testflight
      : `mailto:${LEGAL.email}?subject=${encodeURIComponent(c.cta.inviteSubject)}`;

const getAppPoints = (c: Copy) =>
  CHANNEL === 'appStore'
    ? [c.points.freeToStart, c.points.noAccount]
    : CHANNEL === 'testflight'
      ? [c.points.freeBeta, c.points.noAccount]
      : [c.points.freeBeta, c.points.noAccount, c.points.reply];

type Storage = 'fridge' | 'freezer';

/** The example week: protein per portion and where each lunch waits. Names are in copy. */
const WEEK: { protein: number; storage: Storage }[] = [
  { protein: 41, storage: 'fridge' },
  { protein: 23, storage: 'fridge' },
  { protein: 41, storage: 'fridge' },
  { protein: 23, storage: 'freezer' },
  { protein: 41, storage: 'freezer' },
];
const MAX_PROTEIN = Math.max(...WEEK.map((l) => l.protein));
const AVG_PROTEIN = Math.round(WEEK.reduce((sum, l) => sum + l.protein, 0) / WEEK.length);
const GOAL_GRAMS = [35, 41, 49];

type BaseId = keyof Copy['bases'];

const BASES: { id: BaseId; slot: keyof Copy['slot']; protein: number; fridgeDays: number }[] = [
  { id: 'teriyaki', slot: 'Protein', protein: 32, fridgeDays: 3 },
  { id: 'lachs', slot: 'Protein', protein: 28, fridgeDays: 2 },
  { id: 'linsen', slot: 'Protein', protein: 14, fridgeDays: 3 },
  { id: 'quinoa', slot: 'Carb', protein: 9, fridgeDays: 3 },
  { id: 'reis', slot: 'Carb', protein: 4, fridgeDays: 1 },
  { id: 'ofengemuese', slot: 'Veg', protein: 4, fridgeDays: 3 },
];

/* ─────────── icons (the app's Icon.tsx style: 24 grid, 2 px stroke, round caps) ─────────── */

const ICONS = {
  check: <path d="M5 12.5l4.5 4.5L19 7.5" />,
  x: <path d="M6 6l12 12M18 6L6 18" />,
  plus: <path d="M12 5v14M5 12h14" />,
  link: (
    <>
      <path d="M10 14a4 4 0 005.7 0l3-3a4 4 0 00-5.7-5.7l-1 1" />
      <path d="M14 10a4 4 0 00-5.7 0l-3 3a4 4 0 005.7 5.7l1-1" />
    </>
  ),
  snow: (
    <path d="M12 3v18M4.2 7.5l15.6 9M4.2 16.5l15.6-9M9.5 4.5L12 7l2.5-2.5M9.5 19.5L12 17l2.5 2.5" />
  ),
  fridge: (
    <>
      <rect x="6" y="3" width="12" height="18" rx="2" />
      <path d="M6 10h12M9 6v2M9 13v3" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </>
  ),
  pencil: <path d="M4 20l4-1 10.5-10.5-3-3L5 16z" />,
  week: (
    <>
      <rect x="4" y="5" width="16" height="15" rx="2" />
      <path d="M4 10h16M9 3v4M15 3v4" />
    </>
  ),
  book: (
    <>
      <path d="M5 4h11a3 3 0 013 3v13H8a3 3 0 01-3-3z" />
      <path d="M5 17a3 3 0 013-3h11" />
    </>
  ),
  list: (
    <>
      <path d="M9 7h11M9 12h11M9 17h11" />
      <circle cx="4.5" cy="7" r=".8" />
      <circle cx="4.5" cy="12" r=".8" />
      <circle cx="4.5" cy="17" r=".8" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8.5" r="3.8" />
      <path d="M4.5 20a7.5 7.5 0 0115 0" />
    </>
  ),
};

const Icon = ({ name }: { name: keyof typeof ICONS }) => (
  <svg className="i" viewBox="0 0 24 24" aria-hidden="true">
    {ICONS[name]}
  </svg>
);

/* ─────────── small pieces ─────────── */

/** Staggers a `[data-reveal]` block after its neighbours (see useScrollReveal). */
const revealDelay = (ms: number) => ({ '--reveal-delay': `${ms}ms` }) as CSSProperties;

const img = (name: string) => `/landing/${name}.webp`;

const Wordmark = ({ theme, alt }: { theme: Theme; alt: string }) => (
  <img
    src={theme === 'dark' ? '/landing/wordmark-white.png' : '/landing/wordmark-dark.png'}
    width={876}
    height={268}
    alt={alt}
  />
);

type CtaLocation = 'nav' | 'hero' | 'close';

const GetAppButton = ({
  c,
  location,
  className = '',
}: {
  c: Copy;
  location: CtaLocation;
  className?: string;
}) => {
  const external = CHANNEL !== 'invite';
  return (
    <a
      className={`btn btn-primary ${className}`}
      href={getAppHref(c)}
      target={external ? '_blank' : undefined}
      rel={external ? 'noreferrer' : undefined}
      onClick={() => track('landing_cta_clicked', { location, channel: CHANNEL })}
    >
      {c.cta[CHANNEL]}
    </a>
  );
};

/** The button with what it costs you underneath: free, no account, how the invite works. */
const GetApp = ({ c, location }: { c: Copy; location: CtaLocation }) => (
  <div className="get">
    <GetAppButton c={c} location={location} />
    <ul className="get-points">
      {getAppPoints(c).map((point) => (
        <li key={point}>
          <Icon name="check" />
          {point}
        </li>
      ))}
    </ul>
  </div>
);

const Kicker = ({ n, children }: { n: string; children: ReactNode }) => (
  <p className="kicker">
    <b>{n}</b>
    <span>{children}</span>
  </p>
);

const ProteinChip = ({ grams, c }: { grams: number; c: Copy }) => (
  <span className="mchip">
    <i className="dot" />
    {grams} g {c.protein}
    <sup>*</sup>
  </span>
);

/**
 * A phone frame around an app screen laid out at its real 390 pt width. `zoom` scales
 * it into the frame: (frame width − 20 px bezel) / 390.
 */
const Phone = ({ label, zoom, children }: { label: string; zoom: number; children: ReactNode }) => (
  <div className="phone" role="img" aria-label={label}>
    <div className="screen">
      <div className="app" style={{ zoom }} aria-hidden="true">
        <div className="status">
          <span>9:41</span>
          <span className="bat">
            <i />
          </span>
        </div>
        {children}
      </div>
    </div>
  </div>
);

/** Sun/moon toggle. The icon shows the theme you switch to (see useLandingTheme). */
const ThemeToggle = ({ theme, onToggle, c }: { theme: Theme; onToggle: () => void; c: Copy }) => {
  const label = theme === 'dark' ? c.theme.toLight : c.theme.toDark;
  return (
    <button
      type="button"
      className="theme-toggle"
      data-shows={theme === 'dark' ? 'sun' : 'moon'}
      aria-label={label}
      title={label}
      onClick={onToggle}
    >
      <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
        <mask id="cheftyThemeMask">
          <rect width="24" height="24" fill="#fff" />
          <circle className="tt-bite" cx="17.5" cy="6.5" r="6.5" fill="#000" />
        </mask>
        <circle
          className="tt-core"
          cx="12"
          cy="12"
          r="8"
          fill="currentColor"
          mask="url(#cheftyThemeMask)"
        />
        <path
          className="tt-rays"
          d="M12 1.5v2.2M12 20.3v2.2M1.5 12h2.2M20.3 12h2.2M4.6 4.6l1.5 1.5M17.9 17.9l1.5 1.5M19.4 4.6l-1.5 1.5M6.1 17.9l-1.5 1.5"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          fill="none"
        />
      </svg>
    </button>
  );
};

/* ─────────── app screens inside the phones ─────────── */

const ReviewScreen = ({ c }: { c: Copy }) => {
  const s = c.shakshuka;
  return (
    <div className="px">
      <div className="rv-hello">
        <img src={img('dog-review')} width={96} height={96} alt="" />
        {s.hello}
      </div>
      <div className="rv-title">{s.title}</div>
      <div className="rv-meta">
        <Icon name="link" />
        {s.link}
      </div>
      <div className="rv-meta">
        <Icon name="pencil" />
        {s.readByAi}
      </div>
      <div className="label gap">{s.ingredients}</div>
      {s.items.map(([qty, name]) => (
        <div className="ingrow" key={name}>
          <b>{qty}</b>
          <span>{name}</span>
          <Icon name="x" />
        </div>
      ))}
      <div className="ingrow add">
        <Icon name="plus" />
        {s.addIngredient}
      </div>
      <div className="label gap">{s.method}</div>
      {s.steps.map((step, k) => (
        <div className="ingrow method" key={step}>
          <b>{k + 1}</b>
          <span>{step}</span>
        </div>
      ))}
    </div>
  );
};

const navClicked = (target: string, area: 'header' | 'footer') =>
  track('landing_nav_clicked', { target, area });
const legalClicked = (
  page: 'impressum' | 'privacy' | 'terms' | 'delete_account' | 'cookie_settings',
) => track('landing_legal_clicked', { page });

/* ─────────── page ─────────── */

export const LandingPage: React.FC = () => {
  const { theme, toggle } = useLandingTheme();
  const { lang, choose } = useLandingLang();
  const c = COPY[lang];
  const rootRef = useRef<HTMLDivElement>(null);
  useScrollReveal(rootRef);
  useSectionViews();
  useScrollDepth();
  const year = new Date().getFullYear();
  const today = c.week[0].dish;
  const [step, setStep] = useState(0);

  useEffect(() => {
    const previous = document.title;
    document.title = c.title;
    return () => {
      document.title = previous;
    };
  }, [c.title]);

  return (
    <div className="landing" ref={rootRef}>
      <a className="skip" href="#main">
        {c.skip}
      </a>

      <header className="nav">
        <div className="wrap nav-in">
          <a className="brand" href="#top" aria-label={c.backToTop}>
            <img
              className="brand-icon"
              src="/landing/app-icon.png"
              width={120}
              height={120}
              alt=""
            />
            <Wordmark theme={theme} alt="chefty" />
          </a>
          <nav className="nav-links" aria-label="Page">
            <a href="#week" onClick={() => navClicked('week', 'header')}>
              {c.nav.week}
            </a>
            <a href="#how" onClick={() => navClicked('how', 'header')}>
              {c.nav.how}
            </a>
            <a href="#protein" onClick={() => navClicked('protein', 'header')}>
              {c.nav.protein}
            </a>
            <a href="#pricing" onClick={() => navClicked('pricing', 'header')}>
              {c.nav.pricing}
            </a>
            <a href="#faq" onClick={() => navClicked('faq', 'header')}>
              {c.nav.faq}
            </a>
          </nav>
          <div className="nav-actions">
            <LangMenu
              lang={lang}
              onChoose={(to) => {
                track('landing_language_changed', { from: lang, to });
                choose(to);
              }}
              c={c}
            />
            <ThemeToggle
              theme={theme}
              onToggle={() => {
                track('landing_theme_changed', { to: theme === 'dark' ? 'light' : 'dark' });
                toggle();
              }}
              c={c}
            />
            <GetAppButton c={c} location="nav" className="btn-sm nav-cta" />
          </div>
        </div>
      </header>

      <main id="main">
        {/* ─────────── hero: cook, freeze, thaw, a different lunch ─────────── */}
        <section className="hero" id="top" aria-labelledby="hero-h">
          <div className="wrap">
            <div className="hero-copy">
              <div className="eyebrow-row">
                <span className="tag">{c.hero.tag}</span>
                <span className="eyebrow">{c.hero.eyebrow}</span>
              </div>
              <h1 id="hero-h" className="verbs">
                {c.hero.verbs.map((verb, k) => (
                  <span key={verb} className={k > step ? 'dim' : undefined}>
                    {verb}{' '}
                  </span>
                ))}
              </h1>
              <p className="lead">{c.hero.lead}</p>
              <GetApp c={c} location="hero" />
            </div>
            <HeroSteps c={c} step={step} onStep={setStep} lunchProtein={WEEK[3].protein} />
            <p className="foot">
              <sup>*</sup> {c.hero.foot}
            </p>
          </div>
        </section>

        {/* ─────────── 01 before / after ─────────── */}
        <section className="sec" aria-labelledby="ba-h" data-track-section="before">
          <div className="wrap">
            <Kicker n="01">{c.before.kicker}</Kicker>
            <h2 className="h2" id="ba-h" data-reveal>
              {c.before.h2}
            </h2>
            <div className="ba">
              <div className="ba-col before" data-reveal>
                <h3>{c.before.beforeHead}</h3>
                <ul>
                  {c.before.beforeLines.map((line) => (
                    <li key={line}>
                      <Icon name="x" />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="ba-col after" data-reveal style={revealDelay(120)}>
                <h3>{c.before.afterHead}</h3>
                <ul>
                  {c.before.afterLines.map((line) => (
                    <li key={line}>
                      <Icon name="check" />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ─────────── 02 how it works: crops of the onboarding ─────────── */}
        <section className="sec" id="how" aria-labelledby="how-h" data-track-section="how">
          <div className="wrap">
            <Kicker n="02">{c.how.kicker}</Kicker>
            <h2 className="h2" id="how-h" data-reveal>
              {c.how.h2}
            </h2>
            <ol className="steps">
              <li className="step" data-reveal>
                <span className="num">1</span>
                <h3>{c.how.s1.h}</h3>
                <p>{c.how.s1.p}</p>
                <div className="crop" aria-hidden="true">
                  <div className="scr">
                    <div className="q">{c.how.s1.q}</div>
                    {c.goals.map((g, k) => (
                      <div className={`opt${k === 1 ? ' sel' : ''}`} key={g.name}>
                        <b>{g.name}</b>
                        <span>{g.sub}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </li>
              <li className="step" data-reveal style={revealDelay(80)}>
                <span className="num">2</span>
                <h3>{c.how.s2.h}</h3>
                <p>{c.how.s2.p}</p>
                <div className="crop" aria-hidden="true">
                  <div className="scr">
                    <div className="q">{c.how.s2.q}</div>
                    <div className="stepper">
                      {c.how.s2.people}
                      <span className="ctl">
                        <i>−</i>1<i>+</i>
                      </span>
                    </div>
                    <div className="mini-label">{c.how.s2.lunches}</div>
                    <div className="seg">
                      <span>3–4</span>
                      <span className="on">5</span>
                      <span>7</span>
                    </div>
                    <div className="mini-label">{c.how.s2.prepDay}</div>
                    <div className="days7">
                      {c.days.map((d, k) => (
                        <span key={d} className={k === 6 ? 'on' : undefined}>
                          {d}
                        </span>
                      ))}
                    </div>
                    <p className="hint">{c.how.s2.hint}</p>
                  </div>
                </div>
              </li>
              <li className="step" data-reveal style={revealDelay(160)}>
                <span className="num">3</span>
                <h3>{c.how.s3.h}</h3>
                <p>{c.how.s3.p}</p>
                <div className="crop" aria-hidden="true">
                  <div className="scr">
                    <div className="q">{c.how.s3.q}</div>
                    <div className="swipe-card">
                      <div className="ph">
                        <img src={img('lachs')} width={640} height={640} alt="" loading="lazy" />
                      </div>
                      <div className="body">
                        <b>{c.bases.lachs.name}</b>
                        <span className="mchip">
                          <i className="dot" />
                          {c.perPortion(28)}
                        </span>
                      </div>
                    </div>
                    <div className="take">
                      <span>{c.how.s3.skip}</span>
                      <span className="go">{c.how.s3.take}</span>
                    </div>
                  </div>
                </div>
              </li>
              <li className="step" data-reveal style={revealDelay(240)}>
                <span className="num">4</span>
                <h3>{c.how.s4.h}</h3>
                <p>{c.how.s4.p}</p>
                <div className="crop" aria-hidden="true">
                  <div className="scr">
                    <div className="plan-kick">{c.how.s4.bases}</div>
                    <div className="plan-hero">
                      <span className="big">5</span>
                      <span className="unit">{c.how.s4.dishes}</span>
                    </div>
                    <div className="plan-kick">{c.how.s4.variety}</div>
                    <div className="planrows">
                      {WEEK.slice(0, 4).map((l, k) => (
                        <div className="planrow" key={k}>
                          <b>{c.days[k]}</b>
                          <span>{c.week[k].dish}</span>
                          {l.storage === 'freezer' && <span className="fz">{c.freezer}</span>}
                          <em>{l.protein} g</em>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </li>
            </ol>
          </div>
        </section>

        {/* ─────────── 03 the prep week: the six built-in bases ─────────── */}
        <section className="sec" id="week" aria-labelledby="week-h" data-track-section="week">
          <div className="wrap">
            <Kicker n="03">{c.prepWeek.kicker}</Kicker>
            <h2 className="h2" id="week-h" data-reveal>
              {c.prepWeek.h2}
            </h2>
            <p className="lead" data-reveal>
              {c.prepWeek.lead}
            </p>
            <Carousel
              label={c.prepWeek.h2}
              prevLabel={c.prepWeek.prev}
              nextLabel={c.prepWeek.next}
              onStep={(direction) => track('landing_carousel_stepped', { direction })}
            >
              {BASES.map((b, k) => (
                <li className="cat-card" key={b.id} data-reveal style={revealDelay(k * 60)}>
                  <img
                    src={img(b.id)}
                    width={640}
                    height={640}
                    loading="lazy"
                    alt={c.bases[b.id].alt}
                  />
                  <p className="eyebrow slot">{c.slot[b.slot]}</p>
                  <h3>{c.bases[b.id].name}</h3>
                  <span className="chips">
                    <ProteinChip grams={b.protein} c={c} />
                    <span className="mchip">{c.fridgeDays(b.fridgeDays)}</span>
                  </span>
                </li>
              ))}
            </Carousel>
            <p className="foot">
              <sup>*</sup> {c.prepWeek.foot}
            </p>

            <div className="freezer-note">
              <div data-reveal>
                <h3 className="h3-lg">{c.prepWeek.freezerH}</h3>
                <p className="body-dim">{c.prepWeek.freezerP}</p>
                <div className="strip">
                  <span className="tag">
                    <Icon name="fridge" />
                    {c.prepWeek.strip}
                  </span>
                  <span className="tag">
                    <Icon name="clock" />
                    {c.prepWeek.time}
                  </span>
                </div>
              </div>
              <img
                className="mascot"
                src={img('dog-prep')}
                width={480}
                height={480}
                loading="lazy"
                alt={c.prepWeek.dogAlt}
              />
            </div>
          </div>
        </section>

        {/* ─────────── 04 protein, as a number ─────────── */}
        <section
          className="sec"
          id="protein"
          aria-labelledby="protein-h"
          data-track-section="protein"
        >
          <div className="wrap">
            <Kicker n="04">{c.proteinSec.kicker}</Kicker>
            <h2 className="h2" id="protein-h" data-reveal>
              {c.proteinSec.h2}
            </h2>
            <div className="protein-grid">
              <div data-reveal>
                <div className="bignum">
                  {AVG_PROTEIN}
                  <small>g</small>
                </div>
                <p className="lead">{c.proteinSec.lead}</p>
                <p className="body">{c.proteinSec.body}</p>
              </div>
              <div
                className="bars"
                role="img"
                aria-label={`${c.proteinSec.barsLabel}: ${WEEK.map((l, k) => `${c.days[k]} ${l.protein} g`).join(', ')}`}
                data-reveal
                style={revealDelay(120)}
              >
                {WEEK.map((l, k) => (
                  <div className="bar" key={k} style={{ '--k': k } as CSSProperties}>
                    <span className="v">{l.protein} g</span>
                    <span
                      className="col"
                      style={{ height: `${(l.protein / MAX_PROTEIN) * 100}%` }}
                    />
                    <span className="lbl">
                      {c.days[k]}
                      <span>{c.week[k].short}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="goals" aria-label={c.proteinSec.goalsLabel(today)}>
              {c.goals.map((g, k) => (
                <div className="goal" key={g.name}>
                  <b>{g.name}</b>
                  <div className="gn">
                    {GOAL_GRAMS[k]}
                    <small> g</small>
                  </div>
                  <span>{g.short}</span>
                </div>
              ))}
            </div>
            <p className="foot">{c.proteinSec.foot(today)}</p>
          </div>
        </section>

        {/* ─────────── 05 your own recipes ─────────── */}
        <section
          className="sec"
          id="recipes"
          aria-labelledby="recipes-h"
          data-track-section="recipes"
        >
          <div className="wrap">
            <Kicker n="05">{c.recipes.kicker}</Kicker>
            <h2 className="h2" id="recipes-h" data-reveal>
              {c.recipes.h2}
            </h2>
            <div className="bento">
              <article className="b-import" data-reveal>
                <h3>{c.recipes.importH}</h3>
                <p className="so">{c.recipes.importP}</p>
                <ImportDemo c={c} />
              </article>

              <article className="b-review" data-reveal style={revealDelay(120)}>
                <h3>{c.recipes.reviewH}</h3>
                <p className="so">{c.recipes.reviewP}</p>
                <Phone zoom={260 / 390} label={c.recipes.reviewLabel}>
                  <ReviewScreen c={c} />
                </Phone>
              </article>

              <article className="b-list" data-reveal>
                <h3>{c.recipes.listH}</h3>
                <p className="so">{c.recipes.listP}</p>
                <div className="ui-card list-ui" aria-hidden="true">
                  <b className="hd">{c.recipes.listHead}</b>
                  <div className="num">
                    <strong>12</strong>
                    <span>{c.recipes.items}</span>
                  </div>
                  <div className="cnt">{c.recipes.counter(2, 12)}</div>
                  {(
                    [
                      [
                        c.recipes.aisles.produce,
                        [
                          [c.recipes.shopping.springOnion, '1', true],
                          [c.recipes.shopping.ginger, '50 g', true],
                          [c.recipes.shopping.cucumber, '2', false],
                        ],
                      ],
                      [
                        c.recipes.aisles.chilled,
                        [
                          [c.recipes.shopping.chicken, '500 g', false],
                          [c.recipes.shopping.feta, '200 g', false],
                        ],
                      ],
                    ] as [string, [string, string, boolean][]][]
                  ).map(([aisle, items]) => (
                    <div key={aisle}>
                      <div className="mini-label aisle">{aisle}</div>
                      {items.map(([name, qty, done]) => (
                        <div className={`li${done ? ' on' : ''}`} key={name}>
                          <span className="bx">{done && <Icon name="check" />}</span>
                          <span>{name}</span>
                          <em>{qty}</em>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </article>

              <article className="b-facts" data-reveal>
                <h3>{c.recipes.factsH}</h3>
                <ul className="facts">
                  {c.recipes.facts.map(([head, rest]) => (
                    <li key={head}>
                      <Icon name="check" />
                      <span>
                        <b>{head}</b>
                        {rest}
                      </span>
                    </li>
                  ))}
                </ul>
              </article>
            </div>
            <p className="smallprint">{c.recipes.smallprint}</p>
          </div>
        </section>

        {/* ─────────── 06 cooking mode, the one dark band ─────────── */}
        <section className="sec cook" id="cook" aria-labelledby="cook-h" data-track-section="cook">
          <div className="wrap cook-grid">
            <div>
              <Kicker n="06">{c.cook.kicker}</Kicker>
              <h2 className="h2" id="cook-h" data-reveal>
                {c.cook.h2}
              </h2>
              <ol className="cook-list">
                {c.cook.list.map(([head, sub], k) => (
                  <li key={head} data-reveal style={revealDelay(k * 80)}>
                    <span className="n">0{k + 1}</span>
                    <div>
                      <b>{head}</b>
                      <span>{sub}</span>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
            <CookDemo c={c} />
          </div>
        </section>

        {/* ─────────── 07 pricing ─────────── */}
        <section
          className="sec"
          id="pricing"
          aria-labelledby="pricing-h"
          data-track-section="pricing"
        >
          <div className="wrap">
            <Kicker n="07">{c.pricing.kicker}</Kicker>
            <h2 className="h2" id="pricing-h" data-reveal>
              {c.pricing.h2}
            </h2>
            <div className="plans">
              <article className="plan" data-reveal>
                <h3>{c.pricing.free}</h3>
                <div className="price">{euro(0, c)}</div>
                <ul>
                  {c.pricing
                    .freeLines(LIMITS.FREE_WEEKLY_IMPORTS, LIMITS.IMPORT_QUOTA_WINDOW_DAYS)
                    .map((line) => (
                      <li key={line}>
                        <Icon name="check" />
                        {line}
                      </li>
                    ))}
                </ul>
              </article>
              <article className="plan paid" data-reveal style={revealDelay(120)}>
                <h3>{c.pricing.plus}</h3>
                <div className="price">
                  {euro(PLUS_MONTHLY, c)}
                  <small>{c.pricing.perMonth}</small>
                </div>
                <p className="price-alt">
                  {c.pricing.alt(
                    euro(PLUS_YEARLY, c),
                    euro(Math.round((PLUS_YEARLY / 12) * 100) / 100, c),
                    TRIAL_DAYS,
                  )}
                </p>
                <ul>
                  {c.pricing.plusLines.map((line) => (
                    <li key={line}>
                      <Icon name="check" />
                      {line}
                    </li>
                  ))}
                </ul>
              </article>
            </div>
            <p className="foot">{c.pricing.foot}</p>
          </div>
        </section>

        {/* ─────────── 08 faq ─────────── */}
        <section className="sec" id="faq" aria-labelledby="faq-h" data-track-section="faq">
          <div className="wrap faq-grid">
            <div className="side">
              <Kicker n="08">{c.faq.kicker}</Kicker>
              <h2 className="h2" id="faq-h">
                {c.faq.h2}
              </h2>
              <p className="body-dim">
                {c.faq.write}{' '}
                <a
                  className="ul"
                  href={`mailto:${LEGAL.supportEmail}`}
                  onClick={() => track('landing_contact_clicked', { channel: 'support_email' })}
                >
                  {LEGAL.supportEmail}
                </a>
                .
              </p>
            </div>
            <div className="faq-list">
              {c.faq
                .items(LIMITS.FREE_WEEKLY_IMPORTS, LIMITS.IMPORT_QUOTA_WINDOW_DAYS)
                .map((f, k) => (
                  <details
                    key={f.q}
                    onToggle={(e) => {
                      if (e.currentTarget.open) track('landing_faq_opened', { question: k + 1 });
                    }}
                  >
                    <summary>
                      {f.q}
                      <span className="pm" aria-hidden="true">
                        <Icon name="plus" />
                      </span>
                    </summary>
                    <p>{f.a}</p>
                  </details>
                ))}
            </div>
          </div>
        </section>

        {/* ─────────── closing: the one coral moment ─────────── */}
        <section className="closing" id="get" aria-labelledby="get-h" data-track-section="close">
          <div className="wrap">
            <div className="band" data-reveal>
              <div className="copy">
                <h2 id="get-h">{c.closing.h2}</h2>
                <p className="band-lede">{c.closing.lede}</p>
                <GetApp c={c} location="close" />
              </div>
              <img
                className="dog"
                src={img('dog-sit')}
                width={480}
                height={618}
                loading="lazy"
                alt={c.closing.dogAlt}
              />
            </div>
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="wrap">
          <div className="f-cols">
            <div className="f-brand">
              <img src="/landing/app-icon.png" width={120} height={120} alt="" />
              <p>{c.footer.tagline}</p>
            </div>
            <nav aria-label={c.footer.product}>
              <h4>{c.footer.product}</h4>
              <ul>
                <li>
                  <a href="#week" onClick={() => navClicked('week', 'footer')}>
                    {c.footer.prepWeek}
                  </a>
                </li>
                <li>
                  <a href="#how" onClick={() => navClicked('how', 'footer')}>
                    {c.footer.how}
                  </a>
                </li>
                <li>
                  <a href="#pricing" onClick={() => navClicked('pricing', 'footer')}>
                    {c.footer.pricing}
                  </a>
                </li>
                <li>
                  <a href="#faq" onClick={() => navClicked('faq', 'footer')}>
                    {c.footer.faq}
                  </a>
                </li>
              </ul>
            </nav>
            <nav aria-label={c.footer.help}>
              <h4>{c.footer.help}</h4>
              <ul>
                <li>
                  <a
                    href="/support"
                    onClick={() => track('landing_contact_clicked', { channel: 'support_page' })}
                  >
                    {c.footer.support}
                  </a>
                </li>
                <li>
                  <a
                    href={`mailto:${LEGAL.email}`}
                    onClick={() => track('landing_contact_clicked', { channel: 'hello_email' })}
                  >
                    {LEGAL.email}
                  </a>
                </li>
                <li>
                  <a href="/delete-account" onClick={() => legalClicked('delete_account')}>
                    {c.footer.deleteAccount}
                  </a>
                </li>
              </ul>
            </nav>
            <nav aria-label={c.footer.legal}>
              <h4>{c.footer.legal}</h4>
              <ul>
                <li>
                  <a href="/impressum" lang="de" onClick={() => legalClicked('impressum')}>
                    Impressum
                  </a>
                </li>
                <li>
                  <a href={c.footer.privacy.href} onClick={() => legalClicked('privacy')}>
                    {c.footer.privacy.label}
                  </a>
                </li>
                <li>
                  <a href={c.footer.terms.href} onClick={() => legalClicked('terms')}>
                    {c.footer.terms.label}
                  </a>
                </li>
                {analyticsAvailable && (
                  <li>
                    <button
                      type="button"
                      className="foot-link-btn"
                      onClick={() => {
                        legalClicked('cookie_settings');
                        openConsentSettings();
                      }}
                    >
                      {c.footer.cookies}
                    </button>
                  </li>
                )}
              </ul>
            </nav>
          </div>
          <div className="giant" aria-hidden="true">
            <Wordmark theme={theme} alt="" />
          </div>
          <address className="imprint">
            <b>Impressum.</b> {LEGAL.entity}
            {LEGAL.owner && <>, {LEGAL.owner}</>}
            {LEGAL_ADDRESS && <>. {LEGAL_ADDRESS}.</>}
            {LEGAL.vatId && (
              <>
                {' '}
                {c.footer.vat} {LEGAL.vatId}.
              </>
            )}
            {LEGAL.contentResponsible && (
              <>
                {' '}
                {c.footer.responsible} {LEGAL.contentResponsible}.
              </>
            )}
          </address>
          <p className="copyright">
            © {year} {LEGAL.entity}. {c.footer.trademarks}
          </p>
        </div>
      </footer>

      {/* Solid strip under the home indicator: iOS 26 tints its bottom toolbar from it. */}
      <div className="chrome-tint" aria-hidden="true" />
    </div>
  );
};
